/**
 * Transporte de la captura de actividad (Métricas de Actividad, Fase 2). Habla con
 * `psiplatform-be` (`/api/activity/*`).
 *
 * NO usa `beHttp` a propósito: `beHttp` muestra toasts de error, reintenta, y ante un 401/403
 * redirige al login. Para telemetría eso está mal: nunca debe verse (ni un toast), nunca debe
 * reintentar por su cuenta (el tracker decide con backoff) y jamás debe expulsar a nadie.
 * Todo acá es silencioso y no tira: ante cualquier problema devuelve un resultado tipado.
 */
import { PSI_PLATFORM_BE_URL } from "./config";
import { getValidToken } from "./auth";

export type ActivityEventType =
  | "session_start"
  | "session_end"
  | "screen_view"
  | "screen_leave"
  | "action"
  | "error";

export type ActivityErrorCode =
  | "api_error"
  | "network_error"
  | "timeout"
  | "auth_error"
  | "render_error"
  | "unhandled_rejection";

/** Evento tal cual lo valida el backend (`ActivityEventInputSchema`). Sin texto libre. */
export interface ActivityEventPayload {
  eventId: string;
  sessionId: string;
  tabId: string;
  eventType: ActivityEventType;
  screenKey: string;
  actionKey?: string;
  errorCode?: ActivityErrorCode;
  resultsCount?: number;
  hadResults?: boolean;
  /** Referencia opaca (alfanumérica, máx. 32). Solo `notification_link_clicked`: id del aviso. */
  ref?: string;
  /** Tiempo activo (screen_leave / session_end). En un `error`: ms que tardó la llamada hasta fallar. */
  durationMs?: number;
  /** Solo `error`: detalle sanitizado de la llamada que falló (ver `utils/activityHttpErrors.ts`). */
  httpStatus?: number;
  endpointKey?: string;
  client?: "api-core" | "psiplatform-be" | "other";
  retried?: boolean;
  /** Id de la llamada (`x-request-id`) para buscarla en los logs; solo errores contra psiplatform-be. */
  requestId?: string;
  /** Repeticiones del mismo error agrupadas en este evento (se omite si es 1). */
  occurrences?: number;
  occurredAt: string;
  viewportClass?: "mobile" | "tablet" | "desktop";
  /** Hash del bundle principal del frontend (ver `utils/appVersion.ts`). */
  appVersion?: string;
}

export interface ActivityClientConfig {
  enabled: boolean;
  sampleRate: number;
  flushIntervalMs: number;
  maxBatchSize: number;
}

export type ActivitySendResult =
  /** El backend recibió el lote. `tracked:false` = la captura está apagada para esta psicóloga. */
  | { kind: "ok"; tracked: boolean }
  /** 429 / 5xx / red caída: reintentar más tarde con backoff. */
  | { kind: "retry" }
  /** 401 / 403: la sesión no sirve; dejar de trackear (sin redirigir ni mostrar nada). */
  | { kind: "auth" }
  /** Otro 4xx (ej. 400): el lote no se va a aceptar nunca; descartarlo, no reintentar. */
  | { kind: "drop" };

const REQUEST_TIMEOUT_MS = 8_000;

const authHeaders = (): Record<string, string> | null => {
  const token = getValidToken();
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : null;
};

const isConfig = (v: unknown): v is ActivityClientConfig =>
  !!v &&
  typeof (v as ActivityClientConfig).enabled === "boolean" &&
  typeof (v as ActivityClientConfig).flushIntervalMs === "number" &&
  typeof (v as ActivityClientConfig).maxBatchSize === "number";

/** `null` si no se pudo leer (el tracker lo trata como "apagado" y reintenta más tarde). */
export async function fetchActivityConfig(): Promise<ActivityClientConfig | null> {
  const headers = authHeaders();
  if (!headers) return null;
  try {
    const res = await fetch(`${PSI_PLATFORM_BE_URL}/api/activity/config`, {
      headers,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    return isConfig(body) ? body : null;
  } catch {
    return null;
  }
}

export async function sendActivityEvents(
  events: ActivityEventPayload[],
  options: { keepalive: boolean },
): Promise<ActivitySendResult> {
  const headers = authHeaders();
  if (!headers) return { kind: "auth" };
  try {
    const res = await fetch(`${PSI_PLATFORM_BE_URL}/api/activity/events`, {
      method: "POST",
      headers,
      body: JSON.stringify({ events }),
      // `keepalive` deja que el request sobreviva al cierre de la pestaña (no admite sendBeacon:
      // no puede mandar el header Authorization). Tope del navegador: ~64 KB por request.
      keepalive: options.keepalive,
      signal: options.keepalive ? undefined : AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (res.status === 401 || res.status === 403) return { kind: "auth" };
    if (res.status === 429 || res.status >= 500) return { kind: "retry" };
    if (!res.ok) return { kind: "drop" };
    const body = (await res.json().catch(() => null)) as { tracked?: boolean } | null;
    return { kind: "ok", tracked: body?.tracked !== false };
  } catch {
    return { kind: "retry" };
  }
}
